import { useState, useEffect, useCallback, useRef, useMemo, Component, type ReactNode } from 'react';
import {
  Plus, Trash2, KeyRound, Loader2, Copy, Check,
  Pencil, X, ShieldCheck, Users as UsersIcon, TrendingUp, Bot,
  ToggleLeft, ToggleRight, ExternalLink, Settings as SettingsIcon, Download,
  CalendarDays, Search, FileSpreadsheet,
} from 'lucide-react';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import { ConfirmModal } from '../../components/atoms/Modal';
import { OrgKeysPanel } from './OrgKeysPanel';
import { PolymarketOrgKeysPanel } from './PolymarketOrgKeysPanel';
import { EditLimitsModal } from './EditLimitsModal';
import { OrgBotDefaultsPanel } from './OrgBotDefaultsPanel';
import { CardGrid } from '../../components/cards/cardKit';
import { useOrgScope } from '../../stores/orgScope';
import { computeHeadroom, formatHeadroomDisplay } from '../../lib/limits';
import { formatMoney } from '../../lib/infra/formatters';
import { downloadCsv } from '../../lib/infra/csvExport';
import type { TraderRules } from '../../types/shared/admin';
import { useLocation, useNavigate } from 'react-router-dom';

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-8">
          <p className="text-sm text-red-500">Something went wrong loading this view.</p>
          <button onClick={() => this.setState({ error: null })}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-500">
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface OrgMember {
  id: string;
  username: string;
  role: 'admin' | 'manager' | 'trader';
  orgId: string;
  useOrgKeys?: boolean;
  botsEnabled?: boolean;
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules: TraderRules | null;
  createdAt: string;
}

interface MemberUsage {
  openContracts: number;
  openPositions: number;
  intentContracts: number;
}

interface PositionsByTraderRow {
  userId: string;
  openCount: number;
  openPositionsCount?: number;
  intentContracts?: number;
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules?: TraderRules | null;
}

const EXPORT_PRESET_OPTIONS = [
  { label: 'All', days: null },
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '365D', days: 365 },
] as const;

const DEFAULT_GOOGLE_LEDGER_SPREADSHEET_ID = '1rANLkhICCyHf-PU3hA7xGde5M1-5dbrgmr-XZcy6KMs';
const DEFAULT_GOOGLE_PRODUCTION_LEDGER_SPREADSHEET_ID = '1Mh3gAhPrquklvXJV2pY7hg0AwzpEHjAprKr4h_uIPzo';
const DEFAULT_GOOGLE_PRODUCTION_TARGET_SHEET_TITLE = 'G1 Import - Moving';
const DEFAULT_GOOGLE_PRODUCTION_SOURCE_SHEET_TITLE = 'Moving';
const DEFAULT_GOOGLE_PRODUCTION_FROM_DATE = '2026-05-01';

interface GoogleProductionCounts {
  g1RowCount: number;
  existingG1RowCount: number;
  inserts: number;
  updates: number;
  seen: number;
  existingG1RowsNotSeen: number;
  skipped: number;
  errors: number;
}

interface GoogleProductionSample {
  key?: string;
  rowNumber?: number;
  betDescription?: string;
  trader?: string;
  ticker?: string;
  risk?: string;
  toWin?: string;
  reason?: string;
  changedColumns?: string[];
}

interface GoogleProductionPreview {
  spreadsheetId: string;
  workbookTitle: string;
  workbookKind: 'production' | 'copy';
  sourceSheetTitle: string;
  targetSheetTitle: string;
  targetExists: boolean;
  targetWillBeCreated: boolean;
  confirmationRequired: string;
  fingerprint: string;
  canApply: boolean;
  counts: GoogleProductionCounts;
  samples: {
    inserts: GoogleProductionSample[];
    updates: GoogleProductionSample[];
    seen: GoogleProductionSample[];
    existingG1RowsNotSeen: GoogleProductionSample[];
    skipped: GoogleProductionSample[];
    errors: GoogleProductionSample[];
  };
  applied?: boolean;
  writtenRows?: number;
  valueRangeCount?: number;
}

interface ManagerDashboardProps {
  currentView: string;
  adminMode?: boolean;
  view?: 'settings' | 'exports';
}

export function ManagerDashboard({ currentView, adminMode, view = 'settings' }: ManagerDashboardProps) {
  return (
    <ErrorBoundary>
      <div className="h-full overflow-auto p-6">
        <TradersPanel adminMode={adminMode} view={view} />
      </div>
    </ErrorBoundary>
  );
}

function buildExportUrl(
  endpoint: string,
  orgQueryParam: string,
  options: {
    fromDate: string;
    toDate: string;
    traderIds?: string[];
  },
): string {
  const params = new URLSearchParams(orgQueryParam ? orgQueryParam.slice(1) : '');
  if (options.fromDate) params.set('from', options.fromDate);
  if (options.toDate) params.set('to', options.toDate);
  for (const traderId of options.traderIds ?? []) {
    params.append('traderId', traderId);
  }
  const query = params.toString();
  return query ? `${endpoint}?${query}` : endpoint;
}

async function exportErrorMessage(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      const data = JSON.parse(text) as { error?: unknown };
      if (typeof data.error === 'string') return data.error;
    } catch {
      return text;
    }
  }
  return `Export failed (${res.status})`;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] ?? null;
}

function dateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function exportPresetRange(days: number | null): { fromDate: string; toDate: string } {
  if (days == null) return { fromDate: '', toDate: '' };
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - days);
  return {
    fromDate: dateInputValue(from),
    toDate: dateInputValue(to),
  };
}

function defaultGoogleTestSheetTitle(scope: 'Prof Trades' | 'Org Trades' = 'Prof Trades'): string {
  const now = new Date();
  const stamp = now.toISOString().slice(5, 19).replace('T', ' ').replace(/[-:]/g, '');
  return `G1 Test ${stamp} ${scope}`;
}

function isDefaultGoogleTestSheetTitle(title: string): boolean {
  return /^G1 Test \d{4} \d{6} (Prof|Org) Trades$/.test(title.trim());
}

// ── Role Badge ──────────────────────────────────────────────────

const ROLE_STYLES: Record<string, string> = {
  admin: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  manager: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  trader: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
};

const ROLE_ICONS: Record<string, typeof ShieldCheck> = {
  admin: ShieldCheck,
  manager: UsersIcon,
  trader: TrendingUp,
};

function RoleBadge({ role }: { role: string }) {
  const Icon = ROLE_ICONS[role] ?? TrendingUp;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${ROLE_STYLES[role] ?? ''}`}>
      <Icon size={10} />
      {role}
    </span>
  );
}

// ── Traders Panel ───────────────────────────────────────────────

function TradersPanel({ adminMode, view = 'settings' }: { adminMode?: boolean; view?: 'settings' | 'exports' }) {
  const currentUserId = useAuthStore((s) => s.user?.id) ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [usageByUserId, setUsageByUserId] = useState<Record<string, MemberUsage>>({});
  // Org scope is owned by the shared TabShell layout (one selector across the
  // Organization tabs) via useOrgScope. Non-admin managers always scope to their
  // own org, so selectedOrgId stays '' for them.
  const storeSelectedOrgId = useOrgScope((s) => s.selectedOrgId);
  const storeOrgs = useOrgScope((s) => s.orgs);
  const orgsLoaded = useOrgScope((s) => s.orgsLoaded);
  const ensureOrgs = useOrgScope((s) => s.ensureOrgs);
  const selectedOrgId = adminMode ? storeSelectedOrgId : '';
  const orgs = adminMode ? storeOrgs : [];
  const adminOrgsLoaded = adminMode ? orgsLoaded : true;
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [resetTarget, setResetTarget] = useState<OrgMember | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgMember | null>(null);
  const [limitsTarget, setLimitsTarget] = useState<OrgMember | null>(null);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [selectedExportTraderIds, setSelectedExportTraderIds] = useState<string[]>([]);
  const [exportingTarget, setExportingTarget] = useState<string | null>(null);
  const [googleSpreadsheetId, setGoogleSpreadsheetId] = useState(DEFAULT_GOOGLE_LEDGER_SPREADSHEET_ID);
  const [googleTargetSheetTitle, setGoogleTargetSheetTitle] = useState(defaultGoogleTestSheetTitle);
  const [googleTraderId, setGoogleTraderId] = useState('');
  const [googleAllTraders, setGoogleAllTraders] = useState(false);
  const [googleFromDate, setGoogleFromDate] = useState('');
  const [googleToDate, setGoogleToDate] = useState('');
  const [googleAccessToken, setGoogleAccessToken] = useState('');
  const [googleSourceLabel, setGoogleSourceLabel] = useState('NBAProf');
  const [googleBettorLabel, setGoogleBettorLabel] = useState('The Professor');
  const [googleBookLabel, setGoogleBookLabel] = useState('dash');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleMessage, setGoogleMessage] = useState<string | null>(null);
  const [googleProdSpreadsheetId, setGoogleProdSpreadsheetId] = useState(DEFAULT_GOOGLE_PRODUCTION_LEDGER_SPREADSHEET_ID);
  const [googleProdSourceSheetTitle, setGoogleProdSourceSheetTitle] = useState(DEFAULT_GOOGLE_PRODUCTION_SOURCE_SHEET_TITLE);
  const [googleProdTargetSheetTitle, setGoogleProdTargetSheetTitle] = useState(DEFAULT_GOOGLE_PRODUCTION_TARGET_SHEET_TITLE);
  const [googleProdTraderId, setGoogleProdTraderId] = useState('');
  const [googleProdFromDate, setGoogleProdFromDate] = useState(DEFAULT_GOOGLE_PRODUCTION_FROM_DATE);
  const [googleProdToDate, setGoogleProdToDate] = useState('');
  const [googleProdVenue, setGoogleProdVenue] = useState<'kalshi' | 'polymarket' | 'all'>('kalshi');
  const [googleProdAllowPolymarket, setGoogleProdAllowPolymarket] = useState(false);
  const [googleProdAccessToken, setGoogleProdAccessToken] = useState('');
  const [googleProdSourceLabel, setGoogleProdSourceLabel] = useState('Prof');
  const [googleProdBettorLabel, setGoogleProdBettorLabel] = useState('The Professor');
  const [googleProdBookLabel, setGoogleProdBookLabel] = useState('dash');
  const [googleProdPreview, setGoogleProdPreview] = useState<GoogleProductionPreview | null>(null);
  const [googleProdConfirmation, setGoogleProdConfirmation] = useState('');
  const [googleProdLoading, setGoogleProdLoading] = useState<'preview' | 'apply' | null>(null);
  const [googleProdMessage, setGoogleProdMessage] = useState<string | null>(null);
  const [googleProdError, setGoogleProdError] = useState<string | null>(null);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  // Org-level bot switch — gates the per-trader bot toggle (effective bot access
  // = org.botsEnabled AND user.botsEnabled, so granting a trader is moot while
  // the org switch is off). null = not yet known.
  const [orgBotsEnabled, setOrgBotsEnabled] = useState<boolean | null>(null);

  // Defensive: ensure the admin org list is loaded even if this panel mounts before
  // TabShell's selector. Idempotent — the store guards on its own orgsLoaded flag.
  useEffect(() => {
    if (adminMode) ensureOrgs();
  }, [adminMode, ensureOrgs]);

  const orgQueryParam = adminMode ? `?orgId=${selectedOrgId}` : '';

  // Load the org-level bot switch independently of the settings panel (which may
  // not be mounted in the current view) so the per-trader bot toggle can gate on
  // it. OrgBotDefaultsPanel also reports changes up via onEnabledChange, so a
  // toggle there greys/ungreys the trader chips live.
  useEffect(() => {
    if (adminMode && !selectedOrgId) { setOrgBotsEnabled(null); return; }
    let cancelled = false;
    setOrgBotsEnabled(null);
    (async () => {
      try {
        const res = await apiFetch(`/api/manager/org${orgQueryParam}`);
        const data = await res.json();
        if (!cancelled) setOrgBotsEnabled(res.ok ? !!data.org?.botsEnabled : false);
      } catch {
        if (!cancelled) setOrgBotsEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgQueryParam, adminMode, selectedOrgId]);

  const fetchMembers = useCallback(async () => {
    if (adminMode && !selectedOrgId) { setLoading(false); return; }
    setLoading(true);
    // Two parallel fetches: identity list (limits live on the user row),
    // and the per-trader rollup that gives us live usage for the fill bars.
    // Rollup is best-effort — if it fails we still render the cards without
    // bars rather than blank the whole grid.
    const [tradersRes, usageRes] = await Promise.all([
      apiFetch(`/api/manager/traders${orgQueryParam}`),
      apiFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`).catch(() => null),
    ]);
    const data = await tradersRes.json();
    setMembers(data.traders ?? []);
    if (usageRes && usageRes.ok) {
      const usageData = (await usageRes.json().catch(() => ({}))) as {
        rows?: PositionsByTraderRow[];
      };
      const map: Record<string, MemberUsage> = {};
      for (const r of usageData.rows ?? []) {
        map[r.userId] = {
          openContracts: r.openCount ?? 0,
          openPositions: r.openPositionsCount ?? 0,
          intentContracts: r.intentContracts ?? 0,
        };
      }
      setUsageByUserId(map);
    } else {
      setUsageByUserId({});
    }
    setLoading(false);
  }, [orgQueryParam, adminMode, selectedOrgId]);

  useEffect(() => {
    if (adminMode && !selectedOrgId) return;
    fetchMembers();
  }, [fetchMembers, adminMode, selectedOrgId]);

  useEffect(() => {
    setSelectedExportTraderIds([]);
  }, [orgQueryParam]);

  useEffect(() => {
    const exportableIds = new Set(members.map((m) => m.id));
    setSelectedExportTraderIds((prev) => {
      const next = prev.filter((id) => exportableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [members]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    if (adminMode && !selectedOrgId) return;
    setSubmitting(true); setError(null); setGeneratedPassword(null);
    const res = await apiFetch(`/api/manager/traders${orgQueryParam}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); setSubmitting(false); return; }
    setMembers((prev) => [...prev, data.user]);
    setGeneratedPassword(data.generatedPassword);
    setUsername(''); setShowForm(false); setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await apiFetch(`/api/manager/traders/${deleteTarget.id}${orgQueryParam}`, { method: 'DELETE' });
    if (res.ok) {
      setMembers((prev) => prev.filter((t) => t.id !== deleteTarget.id));
    } else { const data = await res.json(); setError(data.error); }
    setDeleteTarget(null);
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setGeneratedPassword(null);
    const res = await apiFetch(`/api/manager/traders/${resetTarget.id}/reset-password${orgQueryParam}`, { method: 'POST' });
    const data = await res.json();
    if (res.ok) setGeneratedPassword(data.generatedPassword);
    else setError(data.error);
    setResetTarget(null);
  };

  const handleEditSave = async (id: string) => {
    setError(null);
    const res = await apiFetch(`/api/manager/traders/${id}${orgQueryParam}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: editUsername.trim() }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data.user } : m)));
    setEditingId(null);
  };

  const handleToggleUseOrgKeys = async (id: string, next: boolean) => {
    setError(null);
    const isSelf = id === currentUserId;
    const url = isSelf
      ? '/api/manager/me'
      : `/api/manager/traders/${id}${orgQueryParam}`;
    const res = await apiFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ useOrgKeys: next }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data.user } : m)));
  };

  // Per-trader bot access (AND-gated with the org's botsEnabled). Trader rows
  // only — admins/managers don't use the trader bot flow.
  const handleToggleBotsEnabled = async (id: string, next: boolean) => {
    setError(null);
    const res = await apiFetch(`/api/manager/traders/${id}${orgQueryParam}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ botsEnabled: next }),
    });
    const data = await res.json();
    if (!res.ok) { setError(data.error); return; }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...data.user } : m)));
  };

  const downloadExport = async (
    targetKey: string,
    endpoint: string,
    fallbackFilename: string,
    traderIds: string[] = [],
  ) => {
    setExportingTarget(targetKey);
    setError(null);
    setExportMessage(null);
    try {
      const res = await apiFetch(buildExportUrl(endpoint, orgQueryParam, {
        fromDate: exportFromDate,
        toDate: exportToDate,
        traderIds,
      }));
      if (!res.ok) {
        throw new Error(await exportErrorMessage(res));
      }
      const csv = await res.text();
      const filename = filenameFromDisposition(res.headers.get('Content-Disposition')) ?? fallbackFilename;
      downloadCsv(csv, filename);
      const rowCount = res.headers.get('X-Export-Row-Count');
      setExportMessage(
        rowCount == null
          ? `${filename} downloaded.`
          : `${filename} downloaded (${Number(rowCount).toLocaleString()} rows).`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExportingTarget(null);
    }
  };

  const handleOrgExport = () => {
    void downloadExport(
      'org',
      '/api/manager/org/trade-export.csv',
      'org-trade-export.csv',
      selectedExportTraderIds,
    );
  };

  const handleTraderExport = (member: OrgMember) => {
    void downloadExport(
      member.id,
      `/api/manager/traders/${member.id}/trade-export.csv`,
      `trader-${member.username}-trade-export.csv`,
    );
  };

  const handleExportPreset = (days: number | null) => {
    const next = exportPresetRange(days);
    setExportFromDate(next.fromDate);
    setExportToDate(next.toDate);
  };

  const isTrader = (m: OrgMember) => m.role === 'trader';
  const exportMembers = members;

  useEffect(() => {
    if (googleTraderId || members.length === 0) return;
    const prof = members.find((member) => member.username.toLowerCase() === 'prof');
    setGoogleTraderId((prof ?? members[0]).id);
  }, [googleTraderId, members]);

  useEffect(() => {
    if (googleProdTraderId || members.length === 0) return;
    const prof = members.find((member) => member.username.toLowerCase() === 'prof');
    setGoogleProdTraderId((prof ?? members[0]).id);
  }, [googleProdTraderId, members]);

  useEffect(() => {
    setGoogleProdPreview(null);
    setGoogleProdConfirmation('');
  }, [
    googleProdSpreadsheetId,
    googleProdSourceSheetTitle,
    googleProdTargetSheetTitle,
    googleProdTraderId,
    googleProdFromDate,
    googleProdToDate,
    googleProdVenue,
    googleProdAllowPolymarket,
    googleProdSourceLabel,
    googleProdBettorLabel,
    googleProdBookLabel,
    orgQueryParam,
  ]);

  const handleGoogleAllTradersChange = (next: boolean) => {
    setGoogleAllTraders(next);
    setGoogleTargetSheetTitle((current) => (
      isDefaultGoogleTestSheetTitle(current)
        ? defaultGoogleTestSheetTitle(next ? 'Org Trades' : 'Prof Trades')
        : current
    ));
  };

  const handleGoogleSheetLoad = async () => {
    setGoogleLoading(true);
    setError(null);
    setGoogleMessage(null);
    try {
      const res = await apiFetch(`/api/manager/org/google-sheets/moving-test-load${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spreadsheetId: googleSpreadsheetId,
          targetSheetTitle: googleTargetSheetTitle,
          allTraders: googleAllTraders,
          traderId: googleAllTraders ? undefined : googleTraderId,
          from: googleFromDate,
          to: googleToDate,
          venue: 'kalshi',
          sourceLabel: googleAllTraders ? undefined : googleSourceLabel,
          bettorLabel: googleAllTraders ? undefined : googleBettorLabel,
          bookLabel: googleBookLabel,
          accessToken: googleAccessToken,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? 'Google Sheets load failed');
      }
      setGoogleMessage(
        `${data.targetSheetTitle} created (${Number(data.rowCount ?? 0).toLocaleString()} rows).`,
      );
      setGoogleAccessToken('');
      setGoogleTargetSheetTitle(defaultGoogleTestSheetTitle(googleAllTraders ? 'Org Trades' : 'Prof Trades'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google Sheets load failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const googleProductionBody = () => ({
    spreadsheetId: googleProdSpreadsheetId,
    sourceSheetTitle: googleProdSourceSheetTitle,
    targetSheetTitle: googleProdTargetSheetTitle,
    traderId: googleProdTraderId,
    from: googleProdFromDate,
    to: googleProdToDate,
    venue: googleProdVenue,
    allowPolymarket: googleProdAllowPolymarket,
    sourceLabel: googleProdSourceLabel,
    bettorLabel: googleProdBettorLabel,
    bookLabel: googleProdBookLabel,
    accessToken: googleProdAccessToken,
  });

  const handleGoogleProductionPreview = async () => {
    setGoogleProdLoading('preview');
    setError(null);
    setGoogleProdMessage(null);
    setGoogleProdError(null);
    try {
      const res = await apiFetch(`/api/manager/org/google-sheets/moving-production-preview${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(googleProductionBody()),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? 'Google Sheets preview failed');
      }
      setGoogleProdPreview(data as GoogleProductionPreview);
      setGoogleProdMessage(
        `Preview ready: ${Number(data.counts?.inserts ?? 0).toLocaleString()} insert, `
          + `${Number(data.counts?.updates ?? 0).toLocaleString()} update, `
          + `${Number(data.counts?.seen ?? 0).toLocaleString()} seen.`,
      );
    } catch (err) {
      setGoogleProdPreview(null);
      setGoogleProdError(err instanceof Error ? err.message : 'Google Sheets preview failed');
    } finally {
      setGoogleProdLoading(null);
    }
  };

  const handleGoogleProductionApply = async () => {
    if (!googleProdPreview) return;
    setGoogleProdLoading('apply');
    setError(null);
    setGoogleProdMessage(null);
    setGoogleProdError(null);
    try {
      const res = await apiFetch(`/api/manager/org/google-sheets/moving-production-upsert${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...googleProductionBody(),
          previewFingerprint: googleProdPreview.fingerprint,
          confirmation: googleProdConfirmation,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? 'Google Sheets upsert failed');
      }
      setGoogleProdPreview(data as GoogleProductionPreview);
      setGoogleProdMessage(
        `Applied ${Number(data.writtenRows ?? 0).toLocaleString()} row touch`
          + `${Number(data.writtenRows ?? 0) === 1 ? '' : 'es'} to ${data.targetSheetTitle}.`,
      );
      setGoogleProdConfirmation('');
      setGoogleProdAccessToken('');
    } catch (err) {
      setGoogleProdError(err instanceof Error ? err.message : 'Google Sheets upsert failed');
    } finally {
      setGoogleProdLoading(null);
    }
  };

  const orgKeysVisible = !adminMode || (adminMode && !!selectedOrgId);

  return (
    <section>
      {/* The org selector lives in TabShell now; this panel only flags the empty case. */}
      {adminMode && adminOrgsLoaded && orgs.length === 0 && (
        <div className="bg-muted/50 border-border mb-4 rounded-lg border p-6 text-center">
          <p className="text-muted-foreground text-sm">No organizations exist yet.</p>
          <p className="text-muted-foreground mt-1 text-xs">Create one in the Admin panel to manage traders.</p>
        </div>
      )}

      {orgKeysVisible && view === 'settings' && (
        <>
          <div className="mb-6 space-y-6 rounded-lg border border-border bg-card p-4">
            <OrgKeysPanel orgQueryParam={orgQueryParam} embedded />
            <PolymarketOrgKeysPanel orgQueryParam={orgQueryParam} embedded />
          </div>
          <OrgMmRebatePanel orgQueryParam={orgQueryParam} className="mb-6" />
          <OrgBotDefaultsPanel orgQueryParam={orgQueryParam} onEnabledChange={setOrgBotsEnabled} className="mb-6" />
        </>
      )}
      {orgKeysVisible && view === 'exports' && (
        <>
          <div className="mb-6 grid items-stretch gap-4 xl:grid-cols-2">
            <OrgTradeExportPanel
              fromDate={exportFromDate}
              toDate={exportToDate}
              onFromDateChange={setExportFromDate}
              onToDateChange={setExportToDate}
              onPreset={handleExportPreset}
              accounts={exportMembers}
              selectedTraderIds={selectedExportTraderIds}
              onSelectedTraderIdsChange={setSelectedExportTraderIds}
              onExport={handleOrgExport}
              busy={exportingTarget !== null}
              exporting={exportingTarget === 'org'}
              message={exportMessage}
              className="h-full"
            />
            <OrgGoogleSheetLoadPanel
              spreadsheetId={googleSpreadsheetId}
              targetSheetTitle={googleTargetSheetTitle}
              traderId={googleTraderId}
              allTraders={googleAllTraders}
              fromDate={googleFromDate}
              toDate={googleToDate}
              accessToken={googleAccessToken}
              sourceLabel={googleSourceLabel}
              bettorLabel={googleBettorLabel}
              bookLabel={googleBookLabel}
              accounts={exportMembers}
              busy={googleLoading}
              message={googleMessage}
              onSpreadsheetIdChange={setGoogleSpreadsheetId}
              onTargetSheetTitleChange={setGoogleTargetSheetTitle}
              onTraderIdChange={setGoogleTraderId}
              onAllTradersChange={handleGoogleAllTradersChange}
              onFromDateChange={setGoogleFromDate}
              onToDateChange={setGoogleToDate}
              onAccessTokenChange={setGoogleAccessToken}
              onSourceLabelChange={setGoogleSourceLabel}
              onBettorLabelChange={setGoogleBettorLabel}
              onBookLabelChange={setGoogleBookLabel}
              onRun={handleGoogleSheetLoad}
              className="h-full"
            />
          </div>
          {adminMode && (
            <OrgGoogleSheetProductionUpsertPanel
              spreadsheetId={googleProdSpreadsheetId}
              sourceSheetTitle={googleProdSourceSheetTitle}
              targetSheetTitle={googleProdTargetSheetTitle}
              traderId={googleProdTraderId}
              fromDate={googleProdFromDate}
              toDate={googleProdToDate}
              venue={googleProdVenue}
              allowPolymarket={googleProdAllowPolymarket}
              accessToken={googleProdAccessToken}
              sourceLabel={googleProdSourceLabel}
              bettorLabel={googleProdBettorLabel}
              bookLabel={googleProdBookLabel}
              accounts={exportMembers}
              preview={googleProdPreview}
              confirmation={googleProdConfirmation}
              busy={googleProdLoading}
              message={googleProdMessage}
              error={googleProdError}
              onSpreadsheetIdChange={setGoogleProdSpreadsheetId}
              onSourceSheetTitleChange={setGoogleProdSourceSheetTitle}
              onTargetSheetTitleChange={setGoogleProdTargetSheetTitle}
              onTraderIdChange={setGoogleProdTraderId}
              onFromDateChange={setGoogleProdFromDate}
              onToDateChange={setGoogleProdToDate}
              onVenueChange={setGoogleProdVenue}
              onAllowPolymarketChange={setGoogleProdAllowPolymarket}
              onAccessTokenChange={setGoogleProdAccessToken}
              onSourceLabelChange={setGoogleProdSourceLabel}
              onBettorLabelChange={setGoogleProdBettorLabel}
              onBookLabelChange={setGoogleProdBookLabel}
              onConfirmationChange={setGoogleProdConfirmation}
              onPreview={handleGoogleProductionPreview}
              onApply={handleGoogleProductionApply}
              className="mb-6"
            />
          )}
        </>
      )}

      {view === 'settings' && (
        <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Traders</h2>
        <button onClick={() => { setShowForm(!showForm); setGeneratedPassword(null); }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500">
          <Plus size={14} /> Create Trader
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-4 space-y-3 rounded-lg border p-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-xs">Username</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="trader1" />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
              {submitting && <Loader2 size={12} className="animate-spin" />} Create
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </form>
      )}

      <PasswordBanner password={generatedPassword} onDismiss={() => setGeneratedPassword(null)} />
      {error && <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="text-muted-foreground h-5 w-5 animate-spin" /></div>
      ) : members.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">No members yet.</p>
      ) : (
        <CardGrid>
          {members.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              usage={usageByUserId[member.id]}
              editing={editingId === member.id}
              editUsername={editUsername}
              onStartEdit={() => { setEditingId(member.id); setEditUsername(member.username); }}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={() => handleEditSave(member.id)}
              onEditUsernameChange={setEditUsername}
              onResetPassword={isTrader(member) ? () => setResetTarget(member) : undefined}
              onDelete={isTrader(member) ? () => setDeleteTarget(member) : undefined}
              onToggleUseOrgKeys={
                isTrader(member) || member.id === currentUserId
                  ? (next: boolean) => handleToggleUseOrgKeys(member.id, next)
                  : undefined
              }
              onToggleBotsEnabled={
                isTrader(member)
                  ? (next: boolean) => handleToggleBotsEnabled(member.id, next)
                  : undefined
              }
              orgBotsEnabled={orgBotsEnabled}
              onEditLimits={isTrader(member) ? () => setLimitsTarget(member) : undefined}
              onExport={() => handleTraderExport(member)}
              exporting={exportingTarget === member.id}
              onViewDetail={() =>
                navigate(`${location.pathname}/trader/${member.id}`, {
                  state: {
                    username: member.username,
                    orgId: adminMode ? selectedOrgId : undefined,
                  },
                })
              }
            />
          ))}
        </CardGrid>
      )}
        </>
      )}

      <ConfirmModal
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={handleResetPassword}
        title="Reset Password"
        message={`Generate a new password for "${resetTarget?.username}"? The current password will be invalidated immediately.`}
        confirmLabel="Reset Password"
        variant="danger"
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Trader"
        message={`Are you sure you want to delete "${deleteTarget?.username}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />

      <EditLimitsModal
        isOpen={!!limitsTarget}
        onClose={() => setLimitsTarget(null)}
        trader={limitsTarget}
        orgQueryParam={orgQueryParam}
        currentUsage={limitsTarget ? usageByUserId[limitsTarget.id] : undefined}
        onSaved={(next) => {
          setMembers((prev) => prev.map((m) => (m.id === next.id ? { ...m, ...next } : m)));
        }}
      />
    </section>
  );
}

// ── Org MM Rebate Panel ─────────────────────────────────────────
// Org-level opt-in for Kalshi's
// Market Maker Program (the enroll toggle) plus the org-wide expected-rebate
// status — the sum of the per-fill est_rebate_cents the relay stores at fill
// time, over the program's rolling 30-day window. Org-scoped via
// orgQueryParam (manager → own org; admin → selected org).

interface OrgRebateSummary {
  estRebateCents: number;
  fillCount: number;
  windowDays: number;
}

function OrgMmRebatePanel({
  orgQueryParam,
  className = 'mb-6',
}: {
  orgQueryParam: string;
  className?: string;
}) {
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<OrgRebateSummary | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/manager/org/rebate-summary${orgQueryParam}`);
      if (!res.ok) return;
      const s = await res.json();
      setSummary({
        estRebateCents: s.estRebateCents ?? 0,
        fillCount: s.fillCount ?? 0,
        windowDays: s.windowDays ?? 30,
      });
    } catch {
      /* best-effort — status line just stays hidden */
    }
  }, [orgQueryParam]);

  useEffect(() => {
    let cancelled = false;
    setEnabled(null);
    setSummary(null);
    (async () => {
      try {
        const res = await apiFetch(`/api/manager/org${orgQueryParam}`);
        const data = await res.json();
        if (!cancelled) setEnabled(res.ok ? !!data.org?.mmRebateEnabled : false);
      } catch {
        if (!cancelled) setEnabled(false);
      }
    })();
    void loadSummary();
    return () => { cancelled = true; };
  }, [orgQueryParam, loadSummary]);

  const handleToggle = async () => {
    if (enabled === null || saving) return;
    const next = !enabled;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org${orgQueryParam}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mmRebateEnabled: next }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? 'Update failed'); return; }
      setEnabled(!!data.org?.mmRebateEnabled);
      // Pull the change into the caller's own session so their dashboard picks
      // it up without a reload (no-op for an admin acting on another org).
      void refreshMe();
    } catch {
      setError('Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Market Maker Rebate</h3>
        </div>
        <button
          onClick={handleToggle}
          disabled={enabled === null || saving}
          title={enabled ? 'Enrolled — click to disable' : 'Not enrolled — click to enable'}
          className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1.5 text-xs font-medium disabled:opacity-50"
        >
          {saving || enabled === null ? (
            <Loader2 size={16} className="animate-spin" />
          ) : enabled ? (
            <ToggleRight size={18} className="text-emerald-500" />
          ) : (
            <ToggleLeft size={18} className="text-muted-foreground" />
          )}
          <span>{enabled === null ? '' : enabled ? 'Enrolled' : 'Disabled'}</span>
        </button>
      </div>

      <p className="text-muted-foreground mb-3 text-[11px]">
        Enroll this org in Kalshi's Market Maker Program so trade tickets and
        ladders show maker fees net of the rebate (50% pregame, 100% live). The
        rebate settles separately with Kalshi — this controls the in-app
        estimate and the expected-rebate tally below.
      </p>

      {summary && (
        <div className="bg-muted/40 border-border flex items-center justify-between rounded-md border px-3 py-2">
          <div>
            <div className="text-muted-foreground text-[10px] uppercase tracking-wider">
              Est. rebate · last {summary.windowDays}d
            </div>
            <div className="text-muted-foreground/70 text-[10px]">
              {summary.fillCount.toLocaleString()} maker fill
              {summary.fillCount === 1 ? '' : 's'} · uncapped estimate
            </div>
          </div>
          <span className="font-mono text-base font-semibold text-emerald-500">
            {formatMoney(summary.estRebateCents / 100)}
          </span>
        </div>
      )}

      {error && <div className="mt-2 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>}
    </section>
  );
}

function OrgGoogleSheetLoadPanel({
  spreadsheetId,
  targetSheetTitle,
  traderId,
  allTraders,
  fromDate,
  toDate,
  accessToken,
  sourceLabel,
  bettorLabel,
  bookLabel,
  accounts,
  busy,
  message,
  onSpreadsheetIdChange,
  onTargetSheetTitleChange,
  onTraderIdChange,
  onAllTradersChange,
  onFromDateChange,
  onToDateChange,
  onAccessTokenChange,
  onSourceLabelChange,
  onBettorLabelChange,
  onBookLabelChange,
  onRun,
  className = 'mb-6',
}: {
  spreadsheetId: string;
  targetSheetTitle: string;
  traderId: string;
  allTraders: boolean;
  fromDate: string;
  toDate: string;
  accessToken: string;
  sourceLabel: string;
  bettorLabel: string;
  bookLabel: string;
  accounts: OrgMember[];
  busy: boolean;
  message: string | null;
  onSpreadsheetIdChange: (value: string) => void;
  onTargetSheetTitleChange: (value: string) => void;
  onTraderIdChange: (value: string) => void;
  onAllTradersChange: (value: boolean) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onAccessTokenChange: (value: string) => void;
  onSourceLabelChange: (value: string) => void;
  onBettorLabelChange: (value: string) => void;
  onBookLabelChange: (value: string) => void;
  onRun: () => void;
  className?: string;
}) {
  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Sheet Test Load</h3>
        </div>
        <button
          type="button"
          onClick={onRun}
          disabled={busy || (!allTraders && !traderId) || !accessToken || !spreadsheetId || !targetSheetTitle}
          title="Create test tab"
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
          Run
        </button>
      </div>

      <div className="grid gap-2">
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Account</span>
            <select
              value={traderId}
              onChange={(e) => onTraderIdChange(e.target.value)}
              disabled={busy || allTraders}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.username} ({account.role})
                </option>
              ))}
            </select>
          </label>
          <label className="border-border bg-muted/30 flex h-[30px] items-center gap-2 rounded-md border px-2 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={allTraders}
              onChange={(e) => onAllTradersChange(e.target.checked)}
              disabled={busy}
              className="h-3.5 w-3.5"
            />
            <span>All org accounts</span>
          </label>
        </div>

        <label>
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Spreadsheet ID</span>
          <input
            value={spreadsheetId}
            onChange={(e) => onSpreadsheetIdChange(e.target.value)}
            disabled={busy}
            className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
        </label>

        <label>
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">New test tab</span>
          <input
            value={targetSheetTitle}
            onChange={(e) => onTargetSheetTitleChange(e.target.value)}
            disabled={busy}
            className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
        </label>

        <div className="grid gap-2 sm:grid-cols-2">
          <ExportDateInput
            id="sheet-load-from"
            label="From (blank for all history)"
            value={fromDate}
            onChange={onFromDateChange}
            disabled={busy}
          />
          <ExportDateInput
            id="sheet-load-to"
            label="To (blank for now)"
            value={toDate}
            onChange={onToDateChange}
            disabled={busy}
          />
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Originator</span>
            <input
              value={sourceLabel}
              onChange={(e) => onSourceLabelChange(e.target.value)}
              disabled={busy || allTraders}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Bettor</span>
            <input
              value={bettorLabel}
              onChange={(e) => onBettorLabelChange(e.target.value)}
              disabled={busy || allTraders}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Book</span>
            <input
              value={bookLabel}
              onChange={(e) => onBookLabelChange(e.target.value)}
              disabled={busy}
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
        </div>

        <label>
          <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Google access token</span>
          <input
            type="password"
            value={accessToken}
            onChange={(e) => onAccessTokenChange(e.target.value)}
            disabled={busy}
            autoComplete="off"
            className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
        </label>
      </div>

      {message && (
        <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
          {message}
        </div>
      )}
    </section>
  );
}

function OrgGoogleSheetProductionUpsertPanel({
  spreadsheetId,
  sourceSheetTitle,
  targetSheetTitle,
  traderId,
  fromDate,
  toDate,
  venue,
  allowPolymarket,
  accessToken,
  sourceLabel,
  bettorLabel,
  bookLabel,
  accounts,
  preview,
  confirmation,
  busy,
  message,
  error,
  onSpreadsheetIdChange,
  onSourceSheetTitleChange,
  onTargetSheetTitleChange,
  onTraderIdChange,
  onFromDateChange,
  onToDateChange,
  onVenueChange,
  onAllowPolymarketChange,
  onAccessTokenChange,
  onSourceLabelChange,
  onBettorLabelChange,
  onBookLabelChange,
  onConfirmationChange,
  onPreview,
  onApply,
  className = 'mb-6',
}: {
  spreadsheetId: string;
  sourceSheetTitle: string;
  targetSheetTitle: string;
  traderId: string;
  fromDate: string;
  toDate: string;
  venue: 'kalshi' | 'polymarket' | 'all';
  allowPolymarket: boolean;
  accessToken: string;
  sourceLabel: string;
  bettorLabel: string;
  bookLabel: string;
  accounts: OrgMember[];
  preview: GoogleProductionPreview | null;
  confirmation: string;
  busy: 'preview' | 'apply' | null;
  message: string | null;
  error: string | null;
  onSpreadsheetIdChange: (value: string) => void;
  onSourceSheetTitleChange: (value: string) => void;
  onTargetSheetTitleChange: (value: string) => void;
  onTraderIdChange: (value: string) => void;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onVenueChange: (value: 'kalshi' | 'polymarket' | 'all') => void;
  onAllowPolymarketChange: (value: boolean) => void;
  onAccessTokenChange: (value: string) => void;
  onSourceLabelChange: (value: string) => void;
  onBettorLabelChange: (value: string) => void;
  onBookLabelChange: (value: string) => void;
  onConfirmationChange: (value: string) => void;
  onPreview: () => void;
  onApply: () => void;
  className?: string;
}) {
  const polymarketSelected = venue !== 'kalshi';
  const previewDisabled = Boolean(busy)
    || !spreadsheetId.trim()
    || !sourceSheetTitle.trim()
    || !targetSheetTitle.trim()
    || !traderId
    || !accessToken.trim()
    || (polymarketSelected && !allowPolymarket);
  const applyDisabled = Boolean(busy)
    || !preview
    || !preview.canApply
    || !accessToken.trim()
    || confirmation.trim() !== preview.confirmationRequired;

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Production Sheet Upsert</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onPreview}
            disabled={previewDisabled}
            title="Preview sheet upsert"
            className="bg-secondary text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50"
          >
            {busy === 'preview' ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            Preview
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={applyDisabled}
            title="Apply sheet upsert"
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {busy === 'apply' ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Apply
          </button>
        </div>
      </div>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,0.9fr)]">
        <div className="grid gap-2">
          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(8rem,0.5fr)]">
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Spreadsheet ID</span>
              <input
                value={spreadsheetId}
                onChange={(e) => onSpreadsheetIdChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Target tab</span>
              <input
                value={targetSheetTitle}
                onChange={(e) => onTargetSheetTitleChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Source tab</span>
              <input
                value={sourceSheetTitle}
                onChange={(e) => onSourceSheetTitleChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
          </div>

          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_8rem_8rem_8rem]">
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Account</span>
              <select
                value={traderId}
                onChange={(e) => onTraderIdChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.username} ({account.role})
                  </option>
                ))}
              </select>
            </label>
            <ExportDateInput
              id="sheet-prod-from"
              label="From"
              value={fromDate}
              onChange={onFromDateChange}
              disabled={Boolean(busy)}
            />
            <ExportDateInput
              id="sheet-prod-to"
              label="To"
              value={toDate}
              onChange={onToDateChange}
              disabled={Boolean(busy)}
            />
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Venue</span>
              <select
                value={venue}
                onChange={(e) => onVenueChange(e.target.value as 'kalshi' | 'polymarket' | 'all')}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              >
                <option value="kalshi">Kalshi</option>
                <option value="polymarket">Polymarket</option>
                <option value="all">All</option>
              </select>
            </label>
          </div>

          {polymarketSelected && (
            <label className="border-border bg-muted/30 flex items-center gap-2 rounded-md border px-2 py-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={allowPolymarket}
                onChange={(e) => onAllowPolymarketChange(e.target.checked)}
                disabled={Boolean(busy)}
                className="h-3.5 w-3.5"
              />
              <span>Allow non-Kalshi rows in this run</span>
            </label>
          )}

          <div className="grid gap-2 sm:grid-cols-3">
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Originator</span>
              <input
                value={sourceLabel}
                onChange={(e) => onSourceLabelChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Bettor</span>
              <input
                value={bettorLabel}
                onChange={(e) => onBettorLabelChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
            <label>
              <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Book</span>
              <input
                value={bookLabel}
                onChange={(e) => onBookLabelChange(e.target.value)}
                disabled={Boolean(busy)}
                className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </label>
          </div>

          <label>
            <span className="text-muted-foreground mb-1 block text-[11px] font-medium">Google access token</span>
            <input
              type="password"
              value={accessToken}
              onChange={(e) => onAccessTokenChange(e.target.value)}
              disabled={Boolean(busy)}
              autoComplete="off"
              className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </label>
        </div>

        <div className="border-border min-h-48 rounded-md border p-3">
          {preview ? (
            <div className="space-y-3">
              <div className="space-y-1 text-[11px]">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Workbook</span>
                  <span className="text-right font-medium">{preview.workbookTitle || preview.spreadsheetId}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Kind</span>
                  <span className={preview.workbookKind === 'production' ? 'text-amber-500' : 'text-emerald-500'}>
                    {preview.workbookKind}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Tab</span>
                  <span className="text-right">{preview.targetSheetTitle}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Fingerprint</span>
                  <span className="max-w-48 truncate font-mono" title={preview.fingerprint}>{preview.fingerprint}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[11px]">
                <ProductionCount label="G1 rows" value={preview.counts.g1RowCount} />
                <ProductionCount label="Insert" value={preview.counts.inserts} />
                <ProductionCount label="Update" value={preview.counts.updates} />
                <ProductionCount label="Seen" value={preview.counts.seen} />
                <ProductionCount label="Not seen" value={preview.counts.existingG1RowsNotSeen} />
                <ProductionCount label="Errors" value={preview.counts.errors} tone={preview.counts.errors > 0 ? 'red' : undefined} />
              </div>

              <div className="space-y-2">
                <ProductionSampleList title="Insert samples" samples={preview.samples.inserts} />
                <ProductionSampleList title="Update samples" samples={preview.samples.updates} />
                <ProductionSampleList title="Error samples" samples={preview.samples.errors} />
              </div>

              <label>
                <span className="text-muted-foreground mb-1 block text-[11px] font-medium">
                  Confirmation
                </span>
                <input
                  value={confirmation}
                  onChange={(e) => onConfirmationChange(e.target.value)}
                  disabled={Boolean(busy)}
                  placeholder={preview.confirmationRequired}
                  className="bg-background border-border text-foreground w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus:border-blue-500 focus:outline-none disabled:opacity-50"
                />
              </label>
            </div>
          ) : (
            <div className="flex h-full min-h-44 flex-col justify-center gap-2 text-center">
              <p className="text-muted-foreground text-xs">No preview yet.</p>
              <p className="text-muted-foreground/70 text-[11px]">Preview must pass before Apply is enabled.</p>
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
          {message}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {error}
        </div>
      )}
    </section>
  );
}

function ProductionCount({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'red';
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/60 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono font-semibold ${tone === 'red' ? 'text-red-400' : 'text-foreground'}`}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function ProductionSampleList({
  title,
  samples,
}: {
  title: string;
  samples: GoogleProductionSample[];
}) {
  if (samples.length === 0) return null;
  return (
    <div>
      <div className="text-muted-foreground mb-1 text-[10px] uppercase tracking-wider">{title}</div>
      <div className="max-h-28 overflow-auto text-[11px]">
        {samples.map((sample, index) => (
          <div key={`${sample.key ?? sample.reason ?? title}-${index}`} className="border-border/60 border-t py-1 first:border-t-0">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate">{sample.betDescription || sample.key || sample.reason}</span>
              {sample.rowNumber && <span className="text-muted-foreground shrink-0 font-mono">r{sample.rowNumber}</span>}
            </div>
            <div className="text-muted-foreground flex min-w-0 gap-2">
              {sample.trader && <span className="shrink-0">{sample.trader}</span>}
              {sample.ticker && <span className="truncate">{sample.ticker}</span>}
            </div>
            {sample.reason && <div className="text-red-400">{sample.reason}</div>}
            {sample.changedColumns && sample.changedColumns.length > 0 && (
              <div className="text-muted-foreground truncate">
                {sample.changedColumns.join(', ')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function OrgTradeExportPanel({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onPreset,
  accounts,
  selectedTraderIds,
  onSelectedTraderIdsChange,
  onExport,
  busy,
  exporting,
  message,
  className = 'mb-6',
}: {
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onPreset: (days: number | null) => void;
  accounts: OrgMember[];
  selectedTraderIds: string[];
  onSelectedTraderIdsChange: (value: string[]) => void;
  onExport: () => void;
  busy: boolean;
  exporting: boolean;
  message: string | null;
  className?: string;
}) {
  const [traderQuery, setTraderQuery] = useState('');
  const [traderMenuOpen, setTraderMenuOpen] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const activeAccountOptionRef = useRef<HTMLButtonElement | null>(null);
  const accountById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
  const selectedTraderIdSet = useMemo(() => new Set(selectedTraderIds), [selectedTraderIds]);
  const selectedAccounts = selectedTraderIds
    .map((traderId) => accountById.get(traderId))
    .filter((account): account is OrgMember => Boolean(account));
  const filteredAccounts = useMemo(() => {
    const query = traderQuery.trim().toLowerCase();
    return accounts.filter((account) => account.username.toLowerCase().includes(query));
  }, [accounts, traderQuery]);
  const activeAccountIndex = activeAccountId
    ? filteredAccounts.findIndex((account) => account.id === activeAccountId)
    : -1;

  useEffect(() => {
    if (!traderMenuOpen) return;
    if (filteredAccounts.length === 0) {
      if (activeAccountId !== null) setActiveAccountId(null);
      return;
    }
    if (activeAccountIndex === -1) {
      setActiveAccountId(filteredAccounts[0].id);
    }
  }, [activeAccountId, activeAccountIndex, filteredAccounts, traderMenuOpen]);

  useEffect(() => {
    if (!traderMenuOpen) return;
    activeAccountOptionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeAccountId, traderMenuOpen]);

  const firstMatchingAccountId = (query: string) => (
    accounts.find((account) =>
      account.username.toLowerCase().includes(query.trim().toLowerCase()),
    )?.id ?? null
  );

  const toggleTrader = (traderId: string) => {
    onSelectedTraderIdsChange(
      selectedTraderIdSet.has(traderId)
        ? selectedTraderIds.filter((id) => id !== traderId)
        : [...selectedTraderIds, traderId],
    );
    setActiveAccountId(traderId);
  };
  const removeTrader = (traderId: string) => {
    onSelectedTraderIdsChange(selectedTraderIds.filter((id) => id !== traderId));
  };
  const removeLatestTrader = () => {
    const latestTraderId = selectedTraderIds.at(-1);
    if (!latestTraderId) return;
    removeTrader(latestTraderId);
  };
  const updateTraderQuery = (query: string) => {
    setTraderQuery(query);
    setTraderMenuOpen(true);
    setActiveAccountId(firstMatchingAccountId(query));
  };
  const openTraderMenu = () => {
    setTraderMenuOpen(true);
    if (activeAccountIndex === -1) {
      setActiveAccountId(filteredAccounts[0]?.id ?? null);
    }
  };
  const handleTraderKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && traderQuery.length === 0 && selectedTraderIds.length > 0) {
      event.preventDefault();
      removeLatestTrader();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setTraderMenuOpen(true);
      if (filteredAccounts.length === 0) return;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = activeAccountIndex === -1
        ? (direction === 1 ? 0 : filteredAccounts.length - 1)
        : (activeAccountIndex + direction + filteredAccounts.length) % filteredAccounts.length;
      setActiveAccountId(filteredAccounts[nextIndex].id);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      setTraderMenuOpen(true);
      const activeAccount = activeAccountIndex === -1
        ? filteredAccounts[0]
        : filteredAccounts[activeAccountIndex];
      if (activeAccount) {
        toggleTrader(activeAccount.id);
      }
      return;
    }

    if (event.key === 'Escape' && traderMenuOpen) {
      event.preventDefault();
      setTraderMenuOpen(false);
      setTraderQuery('');
    }
  };

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Download size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">CSV Export</h3>
        </div>
        <button
          onClick={onExport}
          disabled={busy}
          title="Export org CSV"
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          Org CSV
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <ExportDateInput
            id="org-export-from"
            label="From (leave blank for all history)"
            value={fromDate}
            onChange={onFromDateChange}
            disabled={busy}
          />
          <ExportDateInput
            id="org-export-to"
            label="To (leave blank for up to now)"
            value={toDate}
            onChange={onToDateChange}
            disabled={busy}
          />
          <div className="flex flex-wrap gap-1.5 pb-0.5">
            {EXPORT_PRESET_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => onPreset(option.days)}
                disabled={busy}
                className="bg-secondary text-muted-foreground hover:text-foreground rounded-md px-2 py-1 text-[11px] font-medium disabled:opacity-50"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="relative"
          onBlur={() => window.setTimeout(() => {
            setTraderMenuOpen(false);
            setTraderQuery('');
          }, 120)}
        >
          <label className="text-muted-foreground mb-1 block text-[11px] font-medium" htmlFor="org-export-traders">
            Traders (leave blank for all)
          </label>
          <div className="bg-background border-border flex min-h-9 flex-wrap items-center gap-1 rounded-md border px-2 py-1 focus-within:border-blue-500">
            {selectedAccounts.map((account) => (
              <span
                key={account.id}
                className="bg-blue-500/15 text-blue-600 dark:text-blue-400 inline-flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium"
              >
                <span className="truncate">{account.username}</span>
                <button
                  type="button"
                  onClick={() => removeTrader(account.id)}
                  disabled={busy}
                  title={`Remove ${account.username}`}
                  className="rounded p-0.5 hover:bg-blue-500/10 disabled:opacity-50"
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            <Search size={13} className="text-muted-foreground shrink-0" />
            <input
              id="org-export-traders"
              value={traderQuery}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={traderMenuOpen}
              aria-controls="org-export-traders-listbox"
              aria-activedescendant={traderMenuOpen && activeAccountId ? `org-export-account-${activeAccountId}` : undefined}
              onChange={(e) => updateTraderQuery(e.target.value)}
              onFocus={openTraderMenu}
              onKeyDown={handleTraderKeyDown}
              disabled={busy}
              placeholder={selectedAccounts.length > 0 ? 'Add trader' : 'All traders'}
              className="text-foreground placeholder:text-muted-foreground min-w-24 flex-1 bg-transparent py-1 text-xs focus:outline-none disabled:opacity-50"
            />
          </div>
          {traderMenuOpen && (
            <div
              id="org-export-traders-listbox"
              role="listbox"
              aria-multiselectable="true"
              className="bg-card border-border absolute z-20 mt-1 max-h-52 w-full overflow-auto rounded-md border shadow-lg"
            >
              {filteredAccounts.length === 0 ? (
                <div className="text-muted-foreground px-3 py-2 text-xs">No traders</div>
              ) : (
                filteredAccounts.map((account) => {
                  const selected = selectedTraderIdSet.has(account.id);
                  const active = activeAccountId === account.id;
                  return (
                    <button
                      key={account.id}
                      id={`org-export-account-${account.id}`}
                      ref={active ? activeAccountOptionRef : undefined}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActiveAccountId(account.id)}
                      onClick={() => toggleTrader(account.id)}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs ${
                        active ? 'bg-muted/70 text-foreground' : 'hover:bg-muted/60'
                      }`}
                    >
                      <span className="min-w-0 truncate">{account.username}</span>
                      <span className="text-muted-foreground ml-auto shrink-0 text-[10px] uppercase">
                        {account.role}
                      </span>
                      <Check
                        size={13}
                        className={selected ? 'text-blue-500 opacity-100' : 'text-muted-foreground opacity-0'}
                      />
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      {message && (
        <div className="mt-3 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
          {message}
        </div>
      )}
    </section>
  );
}

function openDatePicker(input: HTMLInputElement | null): void {
  if (!input) return;
  const maybePicker = input as HTMLInputElement & { showPicker?: () => void };
  if (typeof maybePicker.showPicker === 'function') {
    try {
      maybePicker.showPicker();
      return;
    } catch {
      /* showPicker can throw when the input is disabled or not visible. */
    }
  }
  input.focus();
}

function ExportDateInput({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <label className="min-w-[8.5rem] flex-1">
      <span className="text-muted-foreground mb-1 block text-[11px] font-medium">{label}</span>
      <span className="relative block">
        <button
          type="button"
          onClick={() => openDatePicker(inputRef.current)}
          disabled={disabled}
          title={`Open ${label.toLowerCase()} calendar`}
          className="text-muted-foreground hover:text-foreground absolute left-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 disabled:opacity-50"
        >
          <CalendarDays size={14} />
        </button>
        <input
          ref={inputRef}
          id={id}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="bg-background border-border text-foreground w-full rounded-md border py-1.5 pl-8 pr-2 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-50 [&::-webkit-calendar-picker-indicator]:hidden"
        />
      </span>
    </label>
  );
}

// ── Member Card ─────────────────────────────────────────────────

interface MemberCardProps {
  member: OrgMember;
  usage?: MemberUsage;
  editing: boolean;
  editUsername: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditUsernameChange: (v: string) => void;
  onResetPassword?: () => void;
  onDelete?: () => void;
  onToggleUseOrgKeys?: (next: boolean) => void;
  onToggleBotsEnabled?: (next: boolean) => void;
  /** Org-level bot switch — when false, the per-trader toggle is shown disabled
   *  (granting a trader is moot until org bots are enabled). null = unknown. */
  orgBotsEnabled?: boolean | null;
  onEditLimits?: () => void;
  onExport?: () => void;
  exporting?: boolean;
  onViewDetail: () => void;
}

function MemberCard(props: MemberCardProps) {
  const {
    member, usage, editing, editUsername,
    onStartEdit, onCancelEdit, onSaveEdit, onEditUsernameChange,
    onResetPassword, onDelete, onToggleUseOrgKeys, onToggleBotsEnabled, orgBotsEnabled, onEditLimits, onExport, exporting,
    onViewDetail,
  } = props;
  const isTrader = member.role === 'trader';
  const useOrgKeys = member.useOrgKeys ?? true;
  const botsEnabled = member.botsEnabled ?? false;
  // Effective bot access is org AND user; when the org switch is off the
  // per-trader grant is inert — show the toggle disabled + labeled so it can't
  // claim "on" while granting nothing. null = not yet known (don't gate).
  const orgBotsOff = orgBotsEnabled === false;

  return (
    <div className="bg-card border-border flex flex-col rounded-lg border">
      <div className="p-3">
        <div className="mb-1.5 flex items-start justify-between">
          {editing ? (
            <input value={editUsername} onChange={(e) => onEditUsernameChange(e.target.value)}
              className="bg-background border-border text-foreground rounded border px-2 py-0.5 text-sm font-medium focus:border-blue-500 focus:outline-none" />
          ) : (
            <span className="text-foreground text-sm font-medium">{member.username}</span>
          )}
          <RoleBadge role={member.role} />
        </div>
        <p className="text-muted-foreground text-[11px]">Joined {new Date(member.createdAt).toLocaleDateString()}</p>
      </div>

      <div className="border-border flex items-center justify-between border-t px-3 py-1.5">
        <div className="flex items-center gap-3">
          {onToggleUseOrgKeys ? (
            <button
              onClick={() => onToggleUseOrgKeys(!useOrgKeys)}
              title={useOrgKeys ? 'Uses org keys' : 'Uses personal keys'}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              {useOrgKeys
                ? <ToggleRight size={14} className="text-blue-500" />
                : <ToggleLeft size={14} className="text-amber-500" />
              }
              <span>{useOrgKeys ? 'Org keys' : 'Personal keys'}</span>
            </button>
          ) : null}
          {onToggleBotsEnabled ? (
            <button
              onClick={() => { if (!orgBotsOff) onToggleBotsEnabled(!botsEnabled); }}
              disabled={orgBotsOff}
              title={
                orgBotsOff
                  ? 'Org bots are disabled — enable org bots first; this grant has no effect until then'
                  : botsEnabled
                    ? 'Bot access on — click to revoke'
                    : 'Bot access off — click to grant'
              }
              className={`flex items-center gap-1 text-[11px] ${
                orgBotsOff ? 'text-muted-foreground/50 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Bot
                size={13}
                className={orgBotsOff ? 'text-muted-foreground/50' : botsEnabled ? 'text-emerald-500' : 'text-muted-foreground'}
              />
              <span>{orgBotsOff ? (botsEnabled ? 'Bots on · org off' : 'Bots off') : botsEnabled ? 'Bots on' : 'Bots off'}</span>
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-0.5">
          {editing ? (
            <>
              <button onClick={onSaveEdit} className="rounded p-1 text-green-500 hover:bg-green-500/10"><Check size={14} /></button>
              <button onClick={onCancelEdit} className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><X size={14} /></button>
            </>
          ) : (
            <>
              {isTrader && (
                <button onClick={onStartEdit} title="Edit" className="text-muted-foreground rounded p-1 hover:bg-blue-500/10 hover:text-blue-400"><Pencil size={12} /></button>
              )}
              {onResetPassword && (
                <button onClick={onResetPassword} title="Reset password" className="text-muted-foreground rounded p-1 hover:bg-yellow-500/10 hover:text-yellow-400"><KeyRound size={12} /></button>
              )}
              {onExport && (
                <button
                  onClick={onExport}
                  disabled={exporting}
                  title="Export CSV"
                  className="text-muted-foreground rounded p-1 hover:bg-emerald-500/10 hover:text-emerald-400 disabled:opacity-50"
                >
                  {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                </button>
              )}
              {onDelete && (
                <button onClick={onDelete} title="Delete" className="text-muted-foreground rounded p-1 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={12} /></button>
              )}
            </>
          )}
        </div>
      </div>

      {isTrader && (
        <LimitsRow
          maxContracts={member.maxContracts}
          maxPositions={member.maxPositions}
          traderRules={member.traderRules}
          usage={usage}
          onEdit={onEditLimits}
        />
      )}

      <button
        onClick={onViewDetail}
        className="border-border text-muted-foreground hover:bg-muted/30 hover:text-foreground flex items-center justify-center gap-1.5 border-t px-3 py-2 text-[11px]"
      >
        <ExternalLink size={11} /> View activity
      </button>
    </div>
  );
}

// ── Limits Row (per-card fill bars + edit shortcut) ─────────────

interface LimitsRowProps {
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules: TraderRules | null;
  usage?: MemberUsage;
  onEdit?: () => void;
}

/** Compact one-line summary of trader_rules for the MemberCard footer.
 *  Returns null when nothing is restricted (so we don't render an
 *  uninformative "All allowed" line). */
function summarizeTraderRules(rules: TraderRules | null): string | null {
  if (!rules) return null;
  const parts: string[] = [];
  if (rules.allowedSports !== null && rules.allowedSports.length > 0) {
    parts.push(`${rules.allowedSports.join(', ')} only`);
  }
  if (!rules.nonSportsEnabled) parts.push('no non-sports');
  if (!rules.polymarketEnabled) parts.push('no new Poly');
  return parts.length > 0 ? parts.join(' · ') : null;
}

const STATUS_BAR_COLOURS: Record<string, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
  over: 'bg-red-600',
  unlimited: 'bg-blue-400',
};

function LimitsRow({ maxContracts, maxPositions, traderRules, usage, onEdit }: LimitsRowProps) {
  // Resting buy intent counts toward the contracts denominator so the bar
  // matches what the relay's checkOrderLimit will actually see.
  const contractsCurrent = usage
    ? usage.openContracts + usage.intentContracts
    : 0;
  const positionsCurrent = usage?.openPositions ?? 0;
  const cHead = computeHeadroom(contractsCurrent, maxContracts);
  const pHead = computeHeadroom(positionsCurrent, maxPositions);
  const rulesSummary = summarizeTraderRules(traderRules);

  return (
    <div className="border-border space-y-1.5 border-t px-3 py-2">
      <BarRow
        label="Positions"
        current={positionsCurrent}
        cap={maxPositions}
        status={pHead.status}
        fraction={pHead.fractionUsed}
      />
      <BarRow
        label="Contracts"
        current={contractsCurrent}
        cap={maxContracts}
        status={cHead.status}
        fraction={cHead.fractionUsed}
      />
      {rulesSummary && (
        <div className="flex items-center gap-2 text-[10px] text-amber-600 dark:text-amber-400">
          <span className="w-16 shrink-0">Rules</span>
          <span className="flex-1 truncate" title={rulesSummary}>{rulesSummary}</span>
        </div>
      )}
      {onEdit && (
        <button
          onClick={onEdit}
          title="Edit limits"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[10px]"
        >
          <SettingsIcon size={10} /> Edit limits
        </button>
      )}
    </div>
  );
}

function BarRow({
  label,
  current,
  cap,
  status,
  fraction,
}: {
  label: string;
  current: number;
  cap: number | null;
  status: string;
  fraction: number | null;
}) {
  const widthPct = cap == null || fraction == null
    ? 100
    : Math.max(0, Math.min(100, fraction * 100));
  const bar = STATUS_BAR_COLOURS[status] ?? 'bg-muted-foreground';
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <span className="text-muted-foreground w-16 shrink-0">{label}</span>
      <div className="bg-muted/30 relative h-1.5 flex-1 overflow-hidden rounded">
        <div className={`${bar} h-full`} style={{ width: `${widthPct}%`, opacity: cap == null ? 0.35 : 1 }} />
      </div>
      <span className="text-muted-foreground w-24 shrink-0 text-right tabular-nums">
        {formatHeadroomDisplay(current, cap)}
      </span>
    </div>
  );
}

// ── Shared ──────────────────────────────────────────────────────

function PasswordBanner({
  password,
  onDismiss,
}: {
  password: string | null;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!password) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-4 rounded-lg border border-green-700/50 bg-green-900/20 p-4 dark:border-green-800">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-green-600 dark:text-green-400">Generated Password</span>
        <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground text-xs">Dismiss</button>
      </div>
      <div className="flex items-center gap-2">
        <code className="bg-card border-border flex-1 rounded border px-3 py-1.5 font-mono text-sm text-green-700 dark:text-green-300">
          {password}
        </code>
        <button onClick={handleCopy} title="Copy" className="text-muted-foreground hover:text-foreground hover:bg-muted rounded p-1.5">
          {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
        </button>
      </div>
      <p className="text-muted-foreground mt-2 text-xs">Save this password now. It will not be shown again.</p>
    </div>
  );
}
